import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, InputNumber, message, Modal, Row, Select, Table } from 'antd';
import AuthContext from '../../../contexts/AuthContext';
import { fetchWorkingHoursChanges } from '../../../api/workingHoursChanges';
import { IWorkingHoursChanges } from '../../../types/workingHoursChanges';
import { getColumnSearchProps } from '../../../helpers/tableSearch';
import { DownloadOutlined } from '@ant-design/icons';
import { getMonthName } from '../../../helpers/functions';
import { useForm } from 'antd/lib/form/Form';
import { apiURL } from '../../../api/api';

const WorkingHoursChangesTable: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [workingHoursChanges, setWorkingHoursChanges] = useState<IWorkingHoursChanges[]>();

  const [searchedColumn, setSearchedColumn] = useState();
  const [searchText, setSearchText] = useState('');

  const [form] = useForm();
  const [modalVisible, setModalVisible] = useState(false);

  const now = new Date();
  const months = Array.from({ length: 12 }, (v, k) => k);

  const onReportFormSubmit = async ({ year, month, reportType }) => {
	const a = document.createElement('a');
    document.body.appendChild(a);
    a.href = `${apiURL}/reports/workingHoursChanges?month=${month}&year=${year}&type=${reportType}`;
    a.click();
    document.body.removeChild(a);

    setModalVisible(false);
  };

  useEffect(() => {
	updateData();
  }, []);

  const updateData = async () => {
	setLoading(true);

	try {
	  const workingHoursChanges = await fetchWorkingHoursChanges();
	  setWorkingHoursChanges(workingHoursChanges);
	}
	catch (err) {
	  console.error(err);
	  message.error('Не вдалось отримати дані');
	}
	finally {
	  setLoading(false);
	}
  };

  const columns = [
	{
	  title: 'Дата',
	  dataIndex: 'date',
	  key: 'date',
	  sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
	  sortDirections: ['descend', 'ascend'],
	  showSorterTooltip: false,
	  render: (date) => new Date(date).toLocaleDateString()
	},
	{
	  title: 'Ім\'я робітника',
	  dataIndex: 'fullName',
	  key: 'fullName',
	  sorter: (a, b) => a.fullName.localeCompare(b.fullName),
	  sortDirections: ['descend', 'ascend'],
	  showSorterTooltip: false,
	  ...getColumnSearchProps('fullName', setSearchText, setSearchedColumn)
	},
	{
	  title: 'Об\'єкт',
	  dataIndex: 'objectName',
	  key: 'objectName',
	  sorter: (a, b) => a.objectName.localeCompare(b.objectName),
	  sortDirections: ['descend', 'ascend'],
	  showSorterTooltip: false,
	  ...getColumnSearchProps('objectName', setSearchText, setSearchedColumn)
	},
	{
	  title: 'Годин до зміни',
	  dataIndex: 'workingHoursBefore',
	  key: 'workingHoursBefore',
	  sorter: (a, b) => a.workingHoursBefore - b.workingHoursBefore,
	  sortDirections: ['descend', 'ascend'],
	  showSorterTooltip: false,
	  render: (workingHoursBefore) => workingHoursBefore.toString().concat(' год.'),
	  ...getColumnSearchProps('workingHoursBefore', setSearchText, setSearchedColumn)
	},
	{
	  title: 'Годин після зміни',
	  dataIndex: 'workingHoursAfter',
	  key: 'workingHoursAfter',
	  sorter: (a, b) => a.workingHoursAfter - b.workingHoursAfter,
	  sortDirections: ['descend', 'ascend'],
	  showSorterTooltip: false,
	  render: (workingHoursAfter) => workingHoursAfter.toString().concat(' год.'),
	  ...getColumnSearchProps('workingHoursAfter', setSearchText, setSearchedColumn)
	},
	{
	  title: 'Ким змінено',
	  dataIndex: 'isByEmployee',
	  key: 'isByEmployee',
	  sorter: (a, b) => a.workingHoursAfter - b.workingHoursAfter,
	  sortDirections: ['isByEmployee', 'isByEmployee'],
	  showSorterTooltip: false,
	  render: (isByEmployee) => isByEmployee ? 'Робітником' : 'Адміном',
	  ...getColumnSearchProps('isByEmployee', setSearchText, setSearchedColumn)
	},
	{
	  title: 'Дата зміни',
	  dataIndex: 'createdAt',
	  key: 'createdAt',
	  sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
	  sortDirections: ['descend', 'ascend'],
	  showSorterTooltip: false,
	  defaultSortOrder: 'descend',
	  render: (createdAt) => new Date(createdAt).toLocaleDateString()
	}
  ];

  return (<>
	<Modal
		title="Оберіть параметри звіту"
		okText="Продовжити"
		okButtonProps={{ loading }}
		cancelText="Відмінити"
		destroyOnClose={true}
		onOk={form.submit}
		visible={modalVisible}
		onCancel={() => setModalVisible(false)}
	>
		<Form
			form={form}
			onFinish={onReportFormSubmit as any}
			labelCol={{ span: 7 }}
			labelAlign="left"
			initialValues={{
				month: now.getMonth(),
				year: now.getFullYear()
			}}
		>
			<Form.Item
				name="month"
				label="Місяць"
				rules={[
					{
						required: true,
						message: 'Необхідно обрати місяць!'
					}
				]}
			>
				<Select placeholder="Оберіть місяць">
				{
					months.map(month => (
						<Select.Option value={month} key={month}>
							{getMonthName(month)}
						</Select.Option>
					))
				}
				</Select>
			</Form.Item>

			<Form.Item
				name="year"
				label="Рік"
				rules={[
					{
						required: true,
						message: 'Необхідно ввести рік!'
					}
				]}
			>
				<InputNumber placeholder="Введіть рік" style={{ width: 120 }} min={2019}/>
			</Form.Item>

			<Form.Item
				name="reportType"
				label="Тип звіту"
				rules={[
				{
					required: true,
					message: 'Необхідно обрати тип звіту!'
				}
				]}
			>
				<Select placeholder="Оберіть тип звіту">
					<Select.Option value="all" key="all">
						Усі записи
					</Select.Option>
					<Select.Option value="admin" key="admin">
						Тільки адмін
					</Select.Option>
					<Select.Option value="employee" key="employee">
						Тільки працівники
					</Select.Option>
				</Select>
			</Form.Item>
		</Form>
	</Modal>

	<Row justify="end" style={{ marginBottom: 16 }}>
		<Button
			type="primary"
			icon={<DownloadOutlined/>}
			onClick={() => setModalVisible(true)}
		>
			Завантажити звіт
		</Button>
	</Row>

	<Table
	  loading={loading}
	  //@ts-ignore
	  columns={columns}
	  dataSource={workingHoursChanges}
	  rowKey="id"
	  locale={{
		filterReset: 'Скинути',
		filterConfirm: 'ОК',
		emptyText: 'Нічого не знайдено'
	  }}
	/>
  </>);
}

export default WorkingHoursChangesTable;
